import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import api from "@/lib/api";
import EventCard, { shortDate, OnFire } from "@/components/EventCard";
import { useApp } from "@/context/AppContext";
import { useMeta, eventJsonLd } from "@/lib/seo";
import { GENRES, TYPES } from "@/lib/constants";
import { countryName } from "@/lib/utils_gg";

export function TopEvents() {
  const { t, lang } = useApp();
  const [scope, setScope] = useState("year");
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); api.get("/events/top", { params: { scope } }).then(r => setItems(r.data.items)).catch(() => setItems([])); }, [scope]);
  return (
    <section data-testid="top-events">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-foreground pb-3">
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tighter">{t("topEvents")}</h1>
        <div className="flex border border-foreground" role="tablist" data-testid="top-scope">
          {["year", "all"].map(s => <button key={s} role="tab" aria-selected={scope === s} onClick={() => setScope(s)} className={`px-3 py-2 eyebrow !text-[10px] transition-colors ${scope === s ? "bg-foreground text-background" : "hover:bg-muted"}`} data-testid={`top-scope-${s}`}>{t(s === "year" ? "thisYear" : "allTime")}</button>)}
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t("topEventsHint")}</p>
      <ol className="mt-4 divide-y divide-foreground/30 border-b border-foreground/30" data-testid="top-list">
        {items === null && <li className="py-6 eyebrow">…</li>}
        {items?.length === 0 && <li className="py-6 text-sm text-muted-foreground" data-testid="top-empty">{t("noTopEventsYet")}</li>}
        {items?.map((e, i) => (
          <li key={e.event_id}>
            <Link to={`/events/${e.slug}`} className="flex items-center gap-4 py-3 hover:bg-muted transition-colors" data-testid={`top-item-${i + 1}`}>
              <span className={`font-display text-2xl w-10 shrink-0 ${i < 3 ? "text-primary" : "text-muted-foreground"}`}>{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-display uppercase text-lg leading-tight truncate">{e.name}</span>
                <span className="block text-xs text-muted-foreground">{shortDate(e.start_date, lang)} · {e.city}, {countryName(e.country, lang)} · <span aria-hidden="true" className={`inline-block w-1.5 h-1.5 mr-1 align-middle gg-type-dot-${e.event_type}`} data-testid={`top-type-dot-${i + 1}`} />{t(`types.${e.event_type}`)}</span>
              </span>
              <span className="shrink-0 inline-flex items-center gap-1.5 font-display text-base" data-testid={`top-going-${i + 1}`}><Users className="w-4 h-4 text-primary" />{e.going_count || 0}<OnFire event={e} className="w-4 h-4" /></span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}

const Sel = ({ label, value, onChange, children, testid }) => (
  <label className="block"><span className="eyebrow block mb-1">{label}</span><select className="gg-input" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}>{children}</select></label>
);

export default function Archive() {
  const { t } = useApp();
  useMeta({ title: `${t("topEvents")} & ${t("archive")}`, description: t("seoArchiveDesc"), path: "/archive" });
  const [years, setYears] = useState([]);
  const [f, setF] = useState({ year: "", q: "", event_type: "", genre: "" });
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/events/years").then(r => setYears(r.data.years)).catch(() => {}); }, []);
  useEffect(() => {
    setItems(null);
    const params = { past_only: true, exclude_cancelled: true, limit: 200, sort: "desc",
      ...(f.year ? { year: f.year } : {}), ...(f.q ? { q: f.q } : {}), ...(f.event_type ? { event_type: f.event_type } : {}), ...(f.genre ? { genre: f.genre } : {}) };
    api.get("/events", { params }).then(r => setItems(r.data.items)).catch(() => setItems([]));
  }, [f]);
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="archive-page">
      <TopEvents />
      <section className="mt-14" data-testid="archive-section">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-foreground pb-3">
          <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tighter" id="archive">{t("viewAllPastEvents")}</h2>
          <span className="eyebrow text-muted-foreground">{items ? t("nEvents", { n: items.length }) : "…"}</span>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="archive-filters">
          <Sel label={t("year")} value={f.year} onChange={v => setF({ ...f, year: v })} testid="archive-year"><option value="">{t("allYears")}</option>{years.map(y => <option key={y} value={y}>{y}</option>)}</Sel>
          <label className="block"><span className="eyebrow block mb-1">{t("location")}</span><input className="gg-input" placeholder={t("cityOrCountry")} value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="archive-location" /></label>
          <Sel label={t("eventType")} value={f.event_type} onChange={v => setF({ ...f, event_type: v })} testid="archive-type"><option value="">{t("allTypes")}</option>{TYPES.map(x => <option key={x} value={x}>{t(`types.${x}`)}</option>)}</Sel>
          <Sel label={t("genre")} value={f.genre} onChange={v => setF({ ...f, genre: v })} testid="archive-genre"><option value="">{t("allGenres")}</option>{GENRES.map(g => <option key={g} value={g}>{t(`genres.${g}`)}</option>)}</Sel>
        </div>
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-3 md:gap-4" data-testid="archive-list">
          {items?.map(e => <EventCard key={e.event_id} event={e} />)}
          {items?.length === 0 && <div className="col-span-full text-center py-14 eyebrow" data-testid="archive-empty">{t("noPastEvents")}</div>}
        </div>
      </section>
    </div>
  );
}
